const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const REQUIRED_COLUMNS = [
  { key: 'propertyName', display: 'Property Name', aliases: ['Name'] },
  { key: 'propertyCode', display: 'Property Code', aliases: ['Code'] },
];

export const OPTIONAL_COLUMNS = [
  { key: 'legalName', display: 'Legal Name', aliases: [] },
  { key: 'propertyType', display: 'Property Type', aliases: ['Type'] },
  { key: 'propertyGroup', display: 'Property Group', aliases: ['Group'] },
  { key: 'jobCodeLevel2', display: 'Job Code Level 2', aliases: ['Jobcode Level 2'] },
  { key: 'pmsPropertyId', display: 'PMS Property ID', aliases: [] },
  { key: 'customerPropertyId', display: 'Customer Property ID', aliases: [] },
  { key: 'merchantId', display: 'Merchant ID', aliases: ['MID'] },
  { key: 'roomCount', display: 'Room Count', aliases: [] },
  { key: 'addressLine1', display: 'Address Line 1', aliases: ['Address'] },
  { key: 'addressLine2', display: 'Address Line 2', aliases: [] },
  { key: 'city', display: 'City', aliases: [] },
  { key: 'state', display: 'State', aliases: ['State Province'] },
  { key: 'postalCode', display: 'Postal Code', aliases: ['Zip', 'Zip Code'] },
  { key: 'country', display: 'Country', aliases: [] },
  { key: 'phone', display: 'Phone', aliases: [] },
  { key: 'email', display: 'Email', aliases: [] },
  { key: 'baseCurrency', display: 'Base Currency', aliases: ['Currency'] },
  { key: 'status', display: 'Status', aliases: [] },
  { key: 'timezone', display: 'Timezone', aliases: [] },
  { key: 'latitude', display: 'Latitude', aliases: [] },
  { key: 'longitude', display: 'Longitude', aliases: [] },
  { key: 'cashReconEnabled', display: 'Cash Recon Enabled', aliases: [] },
  { key: 'allowOvertime', display: 'Allow Overtime', aliases: [] },
  { key: 'defaultShiftHours', display: 'Default Shift Hours', aliases: [] },
  { key: 'maxHoursPerEmployee', display: 'Max Hours Per Employee', aliases: [] },
  { key: 'overtimeThresholdHours', display: 'Overtime Threshold Hours', aliases: [] },
  { key: 'laborCostBudget', display: 'Labor Cost Budget', aliases: [] },
  { key: 'isCorporateEntity', display: 'Is Corporate Entity', aliases: ['Corporate Entity'] },
];

export const TEMPLATE_HEADERS = [...REQUIRED_COLUMNS, ...OPTIONAL_COLUMNS].map((column) => column.display);

const INTEGER_KEYS = new Set([
  'roomCount',
  'defaultShiftHours',
  'maxHoursPerEmployee',
  'overtimeThresholdHours',
]);

const DECIMAL_KEYS = new Set(['latitude', 'longitude', 'laborCostBudget']);

const BOOLEAN_KEYS = new Set(['cashReconEnabled', 'allowOvertime', 'isCorporateEntity']);

const DISPLAY_BY_KEY = Object.fromEntries(
  [...REQUIRED_COLUMNS, ...OPTIONAL_COLUMNS].map((column) => [column.key, column.display])
);

const LIMITS = {
  propertyName: 255,
  propertyCode: 100,
  legalName: 255,
  propertyType: 50,
  jobCodeLevel2: 100,
  pmsPropertyId: 255,
  customerPropertyId: 255,
  merchantId: 255,
  addressLine1: 255,
  addressLine2: 255,
  city: 100,
  state: 100,
  country: 100,
  postalCode: 20,
  timezone: 100,
  phone: 50,
  email: 320,
  baseCurrency: 10,
  status: 20,
};

export const normalizeHeader = (value) =>
  String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

const headerMatchNames = (header) => [header.display, ...(header.aliases || [])];

const cellText = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Number.isInteger(value) ? String(value) : String(value);
  }
  return String(value).trim();
};

const parseYesNo = (value) => {
  const normalized = cellText(value).toUpperCase();
  if (!normalized) return null;
  if (['YES', 'Y', 'TRUE', '1'].includes(normalized)) return true;
  if (['NO', 'N', 'FALSE', '0'].includes(normalized)) return false;
  return undefined;
};

const parseNumber = (value, { integer }) => {
  const text = cellText(value).replace(/[$,\s]/g, '');
  if (!text) return null;
  const number = Number(text);
  if (!Number.isFinite(number)) return undefined;
  if (integer && !Number.isInteger(number)) return undefined;
  return number;
};

export const indexColumns = (headerRow) => {
  const keyMap = {};
  (headerRow || []).forEach((cell, index) => {
    const key = normalizeHeader(cell);
    if (key && keyMap[key] === undefined) keyMap[key] = index;
  });

  const columnIndexByKey = {};
  const missing = [];
  [...REQUIRED_COLUMNS, ...OPTIONAL_COLUMNS].forEach((column) => {
    let found;
    for (const name of headerMatchNames(column)) {
      const index = keyMap[normalizeHeader(name)];
      if (index !== undefined) {
        found = index;
        break;
      }
    }
    if (found === undefined) {
      if (REQUIRED_COLUMNS.some((required) => required.key === column.key)) {
        missing.push(column.display);
      }
      return;
    }
    columnIndexByKey[column.key] = found;
  });

  return { missing, columnIndexByKey };
};

const resolvePropertyGroup = (value, propertyGroups) => {
  const text = cellText(value);
  if (!text) return { id: null };
  if (UUID_RE.test(text)) return { id: text };
  const match = (propertyGroups || []).find((group) => {
    const name = group.groupName || group.group_name || '';
    const id = group.propertyGroupId || group.property_group_id || '';
    return normalizeHeader(name) === normalizeHeader(text) || String(id) === text;
  });
  if (!match) {
    return { error: `Property Group "${text}" was not found. Use an existing group name or id.` };
  }
  return { id: match.propertyGroupId || match.property_group_id };
};

/**
 * Maps spreadsheet rows into property create payloads.
 * headerError or rowErrors mean the file should not be submitted.
 */
export const buildPropertyImportRows = (headerRow, dataRows, { tenantId, username, propertyGroups } = {}) => {
  const { missing, columnIndexByKey } = indexColumns(headerRow);
  if (missing.length) {
    return {
      headerError: `Missing required column: ${missing.join(', ')}. The first row must include Property Name and Property Code.`,
      rowErrors: [],
      payload: [],
      columnIndexByKey: null,
    };
  }

  const payload = [];
  const rowErrors = [];
  const seenCodes = new Set();

  (dataRows || []).forEach((row, index) => {
    const rowNumber = index + 2;
    const read = (key) => {
      const columnIndex = columnIndexByKey[key];
      if (columnIndex === undefined) return '';
      return cellText(row[columnIndex]);
    };

    const propertyName = read('propertyName');
    const propertyCode = read('propertyCode');
    const label = propertyCode || propertyName || 'blank row';
    const errors = [];

    if (!propertyName) errors.push('Property Name is required');
    if (!propertyCode) errors.push('Property Code is required');
    if (propertyName.length > LIMITS.propertyName) errors.push(`Property Name must be ${LIMITS.propertyName} characters or fewer`);
    if (propertyCode.length > LIMITS.propertyCode) errors.push(`Property Code must be ${LIMITS.propertyCode} characters or fewer`);

    if (propertyCode) {
      const codeKey = propertyCode.toLowerCase();
      if (seenCodes.has(codeKey)) {
        errors.push(`Property Code "${propertyCode}" is duplicated in this file`);
      } else {
        seenCodes.add(codeKey);
      }
    }

    const item = {
      tenantId,
      propertyName: propertyName || null,
      propertyCode: propertyCode || null,
      createdBy: username || null,
      updatedBy: username || null,
    };

    const assignText = (key, field, { upper, underscore } = {}) => {
      if (columnIndexByKey[key] === undefined) return;
      let value = read(key);
      if (!value) {
        item[field] = null;
        return;
      }
      if (underscore) value = value.replace(/\s+/g, '_');
      if (upper) value = value.toUpperCase();
      const limit = LIMITS[key];
      if (limit && value.length > limit) {
        errors.push(`${DISPLAY_BY_KEY[key] || key} must be ${limit} characters or fewer`);
        return;
      }
      item[field] = value;
    };

    assignText('legalName', 'legalName');
    assignText('propertyType', 'propertyType', { upper: true, underscore: true });
    assignText('jobCodeLevel2', 'jobCodeLevel2');
    assignText('pmsPropertyId', 'pmsPropertyId');
    assignText('customerPropertyId', 'customerPropertyId');
    assignText('merchantId', 'merchantId');
    assignText('addressLine1', 'addressLine1');
    assignText('addressLine2', 'addressLine2');
    assignText('city', 'city');
    assignText('state', 'state');
    assignText('postalCode', 'postalCode');
    assignText('country', 'country');
    assignText('phone', 'phone');
    assignText('email', 'email');
    assignText('baseCurrency', 'baseCurrency', { upper: true });
    assignText('status', 'status', { upper: true });
    assignText('timezone', 'timezone');

    if (columnIndexByKey.propertyGroup !== undefined) {
      const resolved = resolvePropertyGroup(row[columnIndexByKey.propertyGroup], propertyGroups);
      if (resolved.error) errors.push(resolved.error);
      else item.propertyGroupId = resolved.id;
    }

    INTEGER_KEYS.forEach((key) => {
      if (columnIndexByKey[key] === undefined) return;
      const parsed = parseNumber(row[columnIndexByKey[key]], { integer: true });
      if (parsed === undefined) {
        errors.push(`${DISPLAY_BY_KEY[key] || key} must be a whole number`);
        return;
      }
      item[key] = parsed;
    });

    DECIMAL_KEYS.forEach((key) => {
      if (columnIndexByKey[key] === undefined) return;
      const parsed = parseNumber(row[columnIndexByKey[key]], { integer: false });
      if (parsed === undefined) {
        errors.push(`${DISPLAY_BY_KEY[key] || key} must be a number`);
        return;
      }
      item[key] = parsed;
    });

    BOOLEAN_KEYS.forEach((key) => {
      if (columnIndexByKey[key] === undefined) return;
      const parsed = parseYesNo(row[columnIndexByKey[key]]);
      if (parsed === undefined) {
        errors.push(`${DISPLAY_BY_KEY[key] || key} must be Yes or No`);
        return;
      }
      item[key] = parsed;
    });

    if (errors.length) {
      rowErrors.push(`Row ${rowNumber} (${label}): ${errors.join('; ')}`);
      return;
    }
    payload.push(item);
  });

  return {
    headerError: '',
    rowErrors,
    payload,
    columnIndexByKey,
  };
};
